import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { ButtonSmComponent } from 'src/app/components/button-sm/button-sm.component';
import { LoaderDotComponent } from 'src/app/components/loader-dot/loader-dot.component';
import { OutlineButtonSmComponent } from 'src/app/components/outline-button-sm/outline-button-sm.component';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { CustomValidators } from 'src/app/core/constants/validators';
import { UserSession } from 'src/app/core/models/user.model';
import { MyOverlayRef } from 'src/app/core/services/overlay/myoverlay-ref';
import { StorageService } from 'src/app/core/services/storage/storage.service';
import { IndianCurrencyPipe } from 'src/app/shared/pipes/indian-currency.pipe';
import { AvailableBid, QuotedBid } from '../../services/products-api.service';
import { ProductsRepositoryService } from '../../services/products-repository.service';

enum ChatResponder {
  System = 'system',
  User = 'user'
}

@Component({
  standalone: true,
  selector: 'webstore-bidding-overlay',
  templateUrl: './bidding-overlay.component.html',
  styleUrls: ['./bidding-overlay.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LoaderDotComponent,
    ButtonSmComponent,
    OutlineButtonSmComponent,
    IndianCurrencyPipe
  ]
})
export class BiddingOverlayComponent {
  ref = inject<MyOverlayRef<BiddingOverlayComponent, {
    productId: string;
    selling_price: number;
  }>>(MyOverlayRef);
  private productsRepositoryService = inject(ProductsRepositoryService);
  private storageService = inject(StorageService);

  readonly IMAGE_URLS = IMAGE_URLS;
  readonly ChatResponder = ChatResponder;

  // Signal-based state management
  private _loading = signal<boolean>(false);
  readonly loading = this._loading.asReadonly();

  private _bidCount = signal<number>(3);
  readonly bidCount = this._bidCount.asReadonly();

  private _showBiddingInput = signal<boolean>(true);
  readonly showBiddingInput = this._showBiddingInput.asReadonly();

  private _systemUserChatLog = signal<any[]>([]);
  readonly systemUserChatLog = this._systemUserChatLog.asReadonly();

  private _biddingDetails = signal<AvailableBid>({
    price: 0,
    our_quoted_price: 0,
    last_bid_price: 0,
    is_low_bid: false,
    is_acceptable: false
  });
  readonly biddingDetails = this._biddingDetails.asReadonly();

  private _sellingPrice = signal<number>(0);
  readonly sellingPrice = this._sellingPrice.asReadonly();

  private _userData = signal<UserSession>({} as UserSession);
  readonly userData = this._userData.asReadonly();

  // Form controls
  readonly bidInput = new FormControl<string | null>('', [CustomValidators.isNumber, CustomValidators.required] as ValidatorFn[]);
  readonly bidPriceControl = new FormControl('', [CustomValidators.isNumber, CustomValidators.required] as ValidatorFn[]);

  // Computed signals for derived state
  readonly hasBidsLeft = computed(() => this._bidCount() > 0);
  readonly isLastBid = computed(() => this._bidCount() === 1);
  readonly bidCountText = computed(() => {
    const count = this._bidCount();
    return count > 1 ? `${count} Bids` : `${count} Bid`;
  });

  readonly lowBidMessages = [
    "No Deal! the bid you made is too low, make a bid that we cannot refuse.",
    "Deal Breaker! Make a higher offer because the one you proposed is too low.",
    "Not a deal! Make a bid that we cannot refuse since the one you made is too low.",
    "Higher bid needed! Your current offer is a deal breaker for us.",
    "Your current offer is not satisfactory. Please make a new bid that meets our expectations.",
    "We appreciate your interest, but unfortunately, your bid falls short of what we're looking for.",
    "We're looking for a more competitive offer. Please reconsider your bid and make a stronger proposal.",
    "I'm looking for a bit more to make the deal perfect. Can you increase it a little?",
    "We cannot accept your current bid as it doesn't meet our requirements. Please come up with a more compelling offer."
  ];

  readonly closeBidMessages = computed(() => {
    const biddingDetails = this._biddingDetails();
    const rawPrice = biddingDetails?.price || biddingDetails?.our_quoted_price || 0;
    const rounded = rawPrice.toFixed(2);
    const displayPrice = rounded.endsWith('.00') ? parseInt(rounded).toString() : rounded;
    return [
      `Although it's difficult for us, we can still offer you the price of <strong> ₹${displayPrice}/- </strong>`,
      `Despite the difficulty, we can still provide you the price of <strong> ₹${displayPrice}/- </strong>`,
      `We can offer you this product at the price of <strong> ₹${displayPrice}/-, </strong> despite its typically high cost.`,
      `We find this to be very challenging, but we can only offer this item to you for <strong> ₹${displayPrice}/- </strong>`,
      `We understand your situation, but we can only offer a price of <strong> ₹${displayPrice}/- </strong> at this time.`,
      `We're doing our best to meet your expectations, and we're able to offer you <strong> ₹${displayPrice}/- </strong> for this product.`,
      `Our current offer is <strong> ₹${displayPrice}/-, </strong> even though we know it may not be ideal for you.`,
      `Despite the challenge, we're able to extend an offer of <strong> ₹${displayPrice}/- </strong> to you. We hope this works for you.`
    ];
  });

  constructor() {
    // Initialize component using effect
    effect(() => {
      this.initializeComponent();
    });
  }

  private initializeComponent() {
    // Initialize user data safely for SSR using storage service
    const storedUserData = this.storageService.retrieve('userData');
    if (storedUserData) {
      this._userData.set(storedUserData);
    }

    // Initialize chat log with user data
    this._systemUserChatLog.set([{
      responder: ChatResponder.System,
      message: `👋 Hi ${new TitleCasePipe().transform(this._userData()?.user?.first_name || 'there')}, you can bid upto 3 times for this product.`
    },
    {
      responder: ChatResponder.System,
      message: 'Enter your bidding amount.',
      offerAccepted: false
    }]);

    this.resetBidCounter();
    this._sellingPrice.set(this.ref.data?.selling_price || 0);

    // Initialize bidding details from overlay data
    if (this.ref.data) {
      const initialBiddingDetails = {
        price: this.ref.data.selling_price,
        our_quoted_price: this.ref.data.selling_price,
        last_bid_price: 0,
        is_low_bid: false,
        is_acceptable: false
      };
      this._biddingDetails.set(initialBiddingDetails);
    }
  }

  private resetBidCounter() {
    this._bidCount.set(3);
  }

  applyBid() {
    this.bidInput.markAllAsTouched();
    this.appendChatLog();
  }

  private appendChatLog() {
    this.decreaseBidCount();

    const currentBiddingDetails = this._biddingDetails();
    if (!currentBiddingDetails.is_low_bid && !currentBiddingDetails.is_acceptable) {
      if (this._sellingPrice() <= currentBiddingDetails.last_bid_price) {
        this._systemUserChatLog.update(log => [...log, {
          responder: ChatResponder.System,
          message: 'The current selling price of this product is already lower than (or equal to) your proposed bid price.',
          offerAccepted: false
        }]);
      } else {
        this._systemUserChatLog.update(log => [...log, {
          responder: ChatResponder.System,
          message: this.closeBidMessages()[(Math.floor(Math.random() * this.closeBidMessages().length))],
          offerAccepted: false
        }]);
      }
    } else if (currentBiddingDetails.is_low_bid) {
      this._systemUserChatLog.update(log => [...log, {
        responder: ChatResponder.System,
        message: this.lowBidMessages[(Math.floor(Math.random() * this.lowBidMessages.length))],
        offerAccepted: false
      }]);
      this._systemUserChatLog.update(log => [...log, {
        responder: ChatResponder.System,
        message: `The offer price for your current bid is <strong> ₹${currentBiddingDetails.our_quoted_price}/- </strong>`,
        offerAccepted: false
      }]);
    } else if (currentBiddingDetails.is_acceptable) {
      this._systemUserChatLog.update(log => [...log, {
        responder: ChatResponder.System,
        message: `🥳 Congratulations!! Your current bid has been accepted.`,
        offerAccepted: true
      }]);
    }

    this.bidInput.reset();
  }

  private decreaseBidCount() {
    const bidCount = this._bidCount();
    if (bidCount > 0) {
      this._bidCount.set(bidCount - 1);
    }
    if (this._bidCount() < 3) {
      this._showBiddingInput.set(false);
    }
  }

  toggleBiddingInput() {
    this._showBiddingInput.set(true);
  }

  async sendCustomerBid() {
    if (this.bidInput.valid && this.bidInput.value && +this.bidInput.value > 0) {
      this._loading.set(true);
      const product_id = this.ref.data?.productId;
      let biddingDetails: QuotedBid = {
        bidding_price: +this.bidInput.value,
        bidding_count: 3 - this._bidCount(),
        last_bid_price: this._biddingDetails()?.last_bid_price || 0
      };

      if (this._bidCount() < 3) {
        biddingDetails = {
          ...biddingDetails,
          our_quoted_price: this._biddingDetails().our_quoted_price
        };
      }

      const bidLog = `My Bid ₹${this.bidInput.value}/-`;
      this._systemUserChatLog.update(log => [...log, {
        responder: ChatResponder.User,
        message: bidLog,
      }]);

      try {
        const response = await this.productsRepositoryService.sendCustomerBid(product_id, biddingDetails);
        this._loading.set(false);

        // Update bidding details with the API response
        if (response) {
          this._biddingDetails.set(response);
        }

        this.applyBid();

      } catch (error) {
        this._loading.set(false);
      }
    }
  }

  startTheBidAgain() {
    this._systemUserChatLog.set([
      {
        responder: ChatResponder.System,
        message: `👋 Hi ${new TitleCasePipe().transform(this._userData()?.user?.first_name || 'there')}, you can bid upto 3 times for this product.`
      },
      {
        responder: ChatResponder.System,
        message: 'Enter your bidding amount.',
        offerAccepted: false
      }
    ]);

    // Reset to default values
    this._biddingDetails.set({
      price: 0,
      our_quoted_price: 0,
      last_bid_price: 0,
      is_low_bid: false,
      is_acceptable: false
    });

    this._showBiddingInput.set(true);
    this.bidInput.reset();
    this._bidCount.set(3);
  }

  submitTheBid() {
    this.ref.send({
      bid_price: this._biddingDetails().is_acceptable ? this._biddingDetails().last_bid_price : this._biddingDetails().price
    });
  }
}

